import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetCustomers, useSearchCustomers } from "api/customers/service";
import { useDeleteCustomer } from "api/customers/service";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import classNames from "classnames";
import CustomersFilters, { CUSTOMER_ACCOUNT_FILTER_ALL } from "./components/CustomersFilters";
import CustomersCards from "./components/CustomersCards";
import {
  readListState,
  saveListState,
  useClearListStateOnLeave,
  LIST_STATE_FLOWS,
} from "utils/listStateSessionStorage";

const CUSTOMERS_LIST_STATE_KEY = "dealertiq-customers-list-state";

const Customers = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useClearListStateOnLeave(CUSTOMERS_LIST_STATE_KEY, LIST_STATE_FLOWS.customers);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";
  const filterSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const storedStateRef = useRef(readListState(CUSTOMERS_LIST_STATE_KEY));
  const [page, setPage] = useState(() => storedStateRef.current?.page ?? 1);
  const [pageSize, setPageSize] = useState(() => storedStateRef.current?.pageSize ?? 20);
  const [searchInput, setSearchInput] = useState(() => storedStateRef.current?.searchInput ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => (storedStateRef.current?.searchInput ?? "").trim());
  const [accountFilter, setAccountFilter] = useState(
    () => storedStateRef.current?.accountFilter ?? CUSTOMER_ACCOUNT_FILTER_ALL
  );
  const searchTimer = useRef(null);
  const searchHydratedRef = useRef(false);
  const listStateHydratedRef = useRef(false);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      if (!searchHydratedRef.current) {
        searchHydratedRef.current = true;
      } else {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  useEffect(() => {
    if (!listStateHydratedRef.current) {
      listStateHydratedRef.current = true;
      return;
    }

    saveListState(CUSTOMERS_LIST_STATE_KEY, {
      searchInput,
      accountFilter,
      page,
      pageSize,
    });
  }, [searchInput, accountFilter, page, pageSize]);

  const isSearching = debouncedSearch.length >= 2;

  const { data: listData, isLoading: listLoading } = useGetCustomers(
    page,
    pageSize,
    !isSearching,
    { accountFilter }
  );
  const { data: searchData, isFetching: searchFetching } = useSearchCustomers(
    { name: debouncedSearch, ssn: debouncedSearch, accountFilter },
    isSearching
  );

  const customersData = isSearching ? searchData : listData;
  const isLoading = isSearching ? searchFetching : listLoading;

  const serverPageCount = customersData?.pagination?.pageCount;
  useEffect(() => {
    if (isSearching) return;
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page, isSearching]);

  const isTheme2Layout = shellThemeVariant === "2";
  const CustomersTable = isTheme2Layout ? ReactTable2 : ReactTable;

  const deleteCustomerMutation = useDeleteCustomer();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const handleDeleteClick = (id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteCustomerMutation.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const rows = (customersData?.results || customersData?.data || customersData || []).map(
    (customer) => ({
      full_name: customer?.full_name || "-",
      customer_id: customer?.customer_id || "-",
      address: customer?.current_address?.address_1 || "-",
      city: customer?.current_address?.city || "-",
      state: customer?.current_address?.state_name || "-",
      zip: customer?.current_address?.zip_code || "-",
      customer_credit: customer?.customer_credit || "-",
    })
  );

  const columns = [
    { Header: t("id"), accessor: "customer_id" },
    { Header: t("customers.name"), accessor: "full_name" },
    { Header: t("customers.address"), accessor: "address" },
    { Header: t("customers.city"), accessor: "city" },
    { Header: t("customers.state"), accessor: "state" },
    { Header: t("customers.zip"), accessor: "zip" },
    { Header: t("customers.customerCredit"), accessor: "customer_credit" },
    {
      Header: "",
      accessor: "actions",
      Cell: ({ row }) => {
        const id = row.original.customer_id;
        return (
          <div className="text-right">
            {account.permissions.roles_edit && (
              <Button
                color="link"
                size="sm"
                className="btn-icon btn-link like customers-table-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/customers/${id}`);
                }}
              >
                <i className="tim-icons icon-pencil" />
              </Button>
            )}
            {account.permissions.roles_delete && (
              <Button
                color="danger"
                size="sm"
                className="btn-icon btn-link like"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(id);
                }}
                disabled={deleteCustomerMutation.isPending}
              >
                {deleteCustomerMutation.isPending && selectedDeleteId === id ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <i className="tim-icons icon-simple-remove" />
                )}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const customersNoDataText = isSearching
    ? "No customers found for your search."
    : t("customers.noCustomersFound");

  const headerActions =
    account.permissions.roles_add ? (
      <Button
        size="sm"
        style={{ ...activeColorStyle, color: "white" }}
        onClick={() => navigate("/customers/add")}
      >
        {t("customers.addCustomer")}
      </Button>
    ) : null;

  const filters = (
    <CustomersFilters
      isTheme2={isTheme2Layout}
      activeColor={activeColor}
      selectStyles={filterSelectStyles}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
      accountFilter={accountFilter}
      onAccountFilterChange={(value) => {
        setAccountFilter(value);
        setPage(1);
      }}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2Layout ? "13px" : undefined, minWidth: 0, maxWidth: "100%" }}
    >
      <CustomersTable
        activeColor={activeColor}
        data={rows}
        columns={columns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2Layout })}
        manualPagination={!isSearching}
        pageCount={isSearching ? 1 : customersData?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={!isSearching ? customersData?.pagination?.total : undefined}
        pageSize={pageSize}
        noDataText={customersNoDataText}
        loading={isLoading}
        loadingText={t("customers.loading")}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onRowClick={(original) => navigate(`/customers/${original.customer_id}`)}
      />
    </div>
  );


  return (
    <div
      className={classNames("content", "search-customers", {
        "search-customers--theme2": isTheme2Layout,
        "theme2-admin-content-compact": isTheme2Layout,
        "theme2-customers-inset": isTheme2Layout,
      })}
    >
      <CustomersCards
        isTheme2={isTheme2Layout}
        filters={filters}
        table={table}
        headerActions={headerActions}
      />

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("customers.deleteCustomer")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("customers.areYouSureDeleteCustomer")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteCustomerMutation.isPending}
          >
            {t("customers.cancel")}
          </Button>
          <Button
            style={{
              background: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteCustomerMutation.isPending}
          >
            {deleteCustomerMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                {t("customers.deleting")}
              </>
            ) : (
              t("customers.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Customers;
